import React, { useMemo } from "react";
import moment from "moment";
import { isPresent } from "./StaffAnalyticsTab";

const AnalyticsNetworkTrend = ({ allShifts = [], dateFrom, dateTo }) => {
  const weeks = useMemo(() => {
    if (!dateFrom || !dateTo) return [];

    const start = moment(dateFrom, "YYYY-MM-DD").startOf("isoWeek");
    const end = moment(dateTo, "YYYY-MM-DD").endOf("isoWeek");
    const buckets = {};

    let cur = start.clone();
    while (cur.isSameOrBefore(end)) {
      const key = cur.format("YYYY-[W]WW");
      buckets[key] = {
        label: `W${cur.isoWeek()} ${cur.format("D MMM")}`,
        expected: 0,
        present: 0,
      };
      cur.add(1, "week");
    }

    allShifts.forEach(({ att, date }) => {
      if (!date) return;
      if (att?.status_value === "on_leave" || att?.status_value === "day_off") return;
      const key = moment(date, "YYYY-MM-DD").format("YYYY-[W]WW");
      if (!buckets[key]) return;
      buckets[key].expected++;
      if (isPresent(att)) buckets[key].present++;
    });

    return Object.entries(buckets).map(([, b]) => ({
      ...b,
      rate: b.expected > 0 ? Math.round((b.present / b.expected) * 100) : 0,
    }));
  }, [allShifts, dateFrom, dateTo]);

  if (weeks.length === 0) {
    return <div className="sta-empty">No trend data available.</div>;
  }

  const getBarClass = (rate) =>
    rate >= 80 ? "sta-trend__bar--good" : rate >= 60 ? "sta-trend__bar--warn" : "sta-trend__bar--bad";

  const trendSentence = (() => {
    if (weeks.length < 2) return null;
    const first = weeks[0].rate;
    const last  = weeks[weeks.length - 1].rate;
    const diff  = last - first;
    if (diff > 2)  return <div className="sta-trend__sentence sta-delta--up">↑ Improving — up {diff}% from {weeks[0].label}</div>;
    if (diff < -2) return <div className="sta-trend__sentence sta-delta--down">↓ Declining — down {Math.abs(diff)}% from {weeks[0].label}</div>;
    return <div className="sta-trend__sentence sta-delta--flat">→ Flat — no significant change over the period</div>;
  })();

  return (
    <div className="sta-trend">
      <div style={{ display: "flex", alignItems: "flex-end" }}>
        <div className="sta-trend__y-labels">
          <span>100%</span>
          <span>50%</span>
          <span>0%</span>
        </div>
        <div style={{ flex: 1, overflowX: "auto" }}>
          <div className="sta-trend__chart">
            {weeks.map((w) => (
              <div
                key={w.label}
                className="sta-trend__bar-wrap"
                title={`${w.label}: ${w.rate}% attendance (${w.present}/${w.expected})`}
              >
                <div
                  className={`sta-trend__bar ${getBarClass(w.rate)}`}
                  style={{ height: `${w.rate}%` }}
                />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 4, paddingLeft: 4, minWidth: 400 }}>
            {weeks.map((w) => (
              <div
                key={w.label}
                className="sta-trend__x-label"
                style={{ flex: 1, textAlign: "center" }}
              >
                {w.label}
              </div>
            ))}
          </div>
        </div>
      </div>
      {trendSentence}
      <div style={{ marginTop: 8, fontSize: 12, color: "#777" }}>
        Network-wide attendance rate per ISO week. Green ≥80%, Yellow 60–79%, Red &lt;60%.
      </div>
    </div>
  );
};

export default AnalyticsNetworkTrend;
